import React, { useState, useRef, useEffect } from "react";
import "./HomePage.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import justEatImg from "../assets/just-eat.webp";
import uberEatsImg from "../assets/uber-eats.webp";
import deliverooImg from "../assets/deliveroo.webp";
import hero1 from "../assets/hero1.webp";
import hero2 from "../assets/hero4.webp";
import hero3 from "../assets/hero3.webp";
import menu1 from "../assets/menu1.webp";
import menu2 from "../assets/menu2.webp";
import islandHall from "../assets/island1-min.webp";
import mainlandHall from "../assets/mainland1-min.webp";
import foodIcon from "../assets/food-icon.webp";
import { Helmet } from "react-helmet";

const HomePage = () => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const formRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const leftRef = useRef(null);
  const textRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const leftElement = leftRef.current;
    const textElement = textRef.current;
    const rightElement = rightRef.current;

    if (leftElement) observer.observe(leftElement);
    if (textElement) observer.observe(textElement);
    if (rightElement) observer.observe(rightElement);

    return () => {
      if (leftElement) observer.unobserve(leftElement);
      if (textElement) observer.unobserve(textElement);
      if (rightElement) observer.unobserve(rightElement);
    };
  }, []);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
  };

  const slides = [
    {
      image: hero1,
      heading: "DELICIOUS FLAVORS",
      buttonText: "ORDER NOW",
      buttonLink: "/order-online",
    },
    {
      image: hero2,
      heading: "BOOK TABLE",
      buttonText: "RESERVATION",
      buttonLink: "/book-table",
    },
    {
      image: hero3,
      heading: "AUTHENTIC CUISINE",
      buttonText: "ORDER NOW",
      buttonLink: "/order-online",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === slides.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) =>
        prev === reviews.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [reviews.length]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch("/php/reviews.php");
        const data = await response.json();
        setReviews(data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    fetchReviews();
  }, []);

  const handleReviewSubmit = async (event) => {
    event.preventDefault();

    const name = event.target.name.value;
    const review = event.target.review.value;
    const star = rating;

    if (star <= 0 || star > 5) {
      alert("Please select a valid star rating.");
      return;
    }

    const reviewData = { name, star, review };

    try {
      const response = await fetch("/php/reviews.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewData),
      });

      const result = await response.json();
      if (result.success) {
        window.location.reload();

        setTimeout(() => {
          const reviewSection = document.getElementById("reviewSection");
          if (reviewSection) {
            reviewSection.scrollIntoView({ behavior: "smooth" });
          }
        }, 500);
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Error submitting review:", error);
    }
  };

  const handleReviewFormToggle = () => {
    setShowReviewForm(!showReviewForm);
    setRating(0);

    if (!showReviewForm) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  return (
    <div className="home-page">
      <Helmet>
        <title>
          LasGidi Restaurant | Authentic Nigerian Cuisine in Manchester
        </title>
        <meta
          name="description"
          content="LasGidi Restaurant offers authentic Nigerian cuisine in Manchester. Explore our menu, book a table, or order online for delicious meals."
        />
        <meta
          name="keywords"
          content="LasGidi Restaurant, Nigerian Cuisine, Manchester Restaurant, Food Delivery, Buffet, Event Halls, Book Table"
        />
        <meta
          property="og:title"
          content="LasGidi Restaurant | Authentic Nigerian Cuisine in Manchester"
        />
        <meta
          property="og:description"
          content="LasGidi Restaurant offers authentic Nigerian cuisine in Manchester. Explore our menu, book a table, or order online for delicious meals."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="hero-section">
        <div className="carousel-container">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`carousel-slide ${
                index === currentIndex ? "active" : ""
              }`}
            >
              <img
                src={slide.image}
                alt="Slide-Image"
                className="carousel-image"
              />
              <div className="carousel-overlay"></div>
              <div className="carousel-text">
                <h1>{slide.heading}</h1>
                <a href={slide.buttonLink} className="carousel-button">
                  {slide.buttonText}
                </a>
              </div>
            </div>
          ))}

          <button className="carousel-arrow left" onClick={prevSlide}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="carousel-arrow right" onClick={nextSlide}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      {/* Hero Banner */}
      <section className="hero-banner">
        <div className="hero-banner-content">
          <section className="cta-section talent-cta">
            <div className="cta-container">
              <div className="cta-overlay">
                <h2>Join Lasgidi Got Talent!</h2>
                <p>
                  Do you have a talent you believe deserves a stage? <br></br>
                  Lasgidi Restaurant is offering you an opportunity to showcase
                  your craft and skill. <b>Send in your application today!</b>
                </p>
                <a href="/lasgidi-got-talent" className="post-review-btn">
                  Register Now
                </a>
              </div>
            </div>
          </section>

          <div className="hero-banner-text">
            <img src={foodIcon} alt="food-icon"></img>
            <h4>
              Lasgidi Restaurant is where Flavors, Dance and Celebrations come
              alive. We redefine dining with our unique blend of a restaurant,
              game lounge and party destination, all under one roof
            </h4>
          </div>

          <div className="lkonowon">
            <h5>Lasgidi Restaurant now on:</h5>
            <div className="lkonowon-imgs">
              <img src={justEatImg} alt="justeat-logo"></img>
              <img src={uberEatsImg} alt="ubereats-logo"></img>
              <img src={deliverooImg} alt="deliveroo-logo"></img>
            </div>
          </div>
        </div>
      </section>

      <div className="menu-section">
        {/* Left Image */}
        <div className="menu-left" ref={leftRef}>
          <img src={menu1} alt="Left Menu" className="menu-image" />
        </div>

        {/* Text Content */}
        <div className="menu-text-container" ref={textRef}>
          <h1 className="menu-background-text">MENU</h1>
          <p>
            Our dedication and love for what we do have crafted an experience
            that resonates with guests, inspiring them to return time and again
            and share their memorable moments with others.
          </p>
          <a href="/order-online" className="view-menu-button">
            View Menu
          </a>
        </div>

        {/* Right Image */}
        <div className="menu-right" ref={rightRef}>
          <img src={menu2} alt="Right Menu" className="menu-image" />
        </div>
      </div>

      <section className="menu-categories">
        <h2>SOME OF OUR MENUS</h2>
        <div className="menu-items">
          <div className="menu-item">
            <i className="fa-solid fa-utensils"></i>
            <h3>Starters</h3>
            <a href="/order-online?category=starters">VIEW MORE</a>
          </div>
          <div className="menu-item">
            <i className="fa-solid fa-bowl-food"></i>
            <h3>Main Menu</h3>
            <a href="/order-online?category=main menu">VIEW MORE</a>
          </div>
          <div className="menu-item">
            <i className="fa-solid fa-clock"></i>
            <h3>Pre-order</h3>
            <a href="/order-online?category=pre-order">VIEW MORE</a>
          </div>
          <div className="menu-item">
            <i className="fa-solid fa-martini-glass"></i>
            <h3>Drinks</h3>
            <a href="/order-online?category=drinks">VIEW MORE</a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials" id="reviewSection">
        <h2>Customer Reviews</h2>
        {reviews.length > 0 ? (
          <div className="testimonial-container">
            <button className="nav-button prev" onClick={prevTestimonial}>
              &lt;
            </button>

            <div className="testimonial-slider">
              <div
                className="testimonial-wrapper"
                style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
              >
                {reviews.map((review, index) => (
                  <div key={index} className="testimonial">
                    <i className="fas fa-quote-left quote-icon"></i>
                    <p className="review-text">"{review.review}"</p>
                    <p className="review-name">{review.name}</p>
                    <div className="stars">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={
                            i < review.star ? "fas fa-star" : "far fa-star"
                          }
                        ></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button className="nav-button next" onClick={nextTestimonial}>
              &gt;
            </button>
          </div>
        ) : (
          <p>No reviews yet</p>
        )}

        <button
          className="cta-button primary-btn post-review-btn"
          onClick={handleReviewFormToggle}
        >
          Post A Review
        </button>

        {/* Review Form */}
        {showReviewForm && (
          <div className="review-form" ref={formRef}>
            <h3>Post Your Review</h3>
            <form onSubmit={handleReviewSubmit}>
              <label>Name:</label>
              <input type="text" name="name" required />

              {/* Star Rating Input */}
              <label>Star Rating:</label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i
                    key={star}
                    className={
                      star <= (hover || rating) ? "fas fa-star" : "far fa-star"
                    } // Filled if hovered or selected
                    onMouseEnter={() => setHover(star)} // Set hover state to current star
                    onMouseLeave={() => setHover(0)} // Reset hover state when leaving
                    onClick={() => setRating(star)} // Set rating state to current star
                  ></i>
                ))}
              </div>

              <label>Review:</label>
              <textarea name="review" required rows="5"></textarea>

              <button
                type="submit"
                className="cta-button primary-btn post-review-btn"
              >
                Submit Review
              </button>
            </form>
          </div>
        )}
      </section>

      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-overlay">
            <h2>Reserve Your Table Today</h2>
            <p>
              Experience exquisite dining in a cozy atmosphere. Book your table
              now and indulge in culinary perfection.
            </p>
            <a href="/book-table" className="post-review-btn">
              Book Now
            </a>
          </div>
        </div>
      </section>

      <section className="halls-section">
        {/* Intro Text */}
        <div className="halls-intro">
          <h2>Our Halls</h2>
          <p>
            We have two elegant event halls to suit your needs, whether you're
            planning an intimate gathering or a grand celebration.
          </p>
        </div>

        {/* Halls Display */}
        <div className="eventhalls-container">
          <div className="event-hall">
            <img src={islandHall} alt="Island Hall" />
            <div className="hall-name">Island Hall</div>
          </div>
          <div className="event-hall">
            <img src={mainlandHall} alt="Mainland Hall" />
            <div className="hall-name">Mainland Hall</div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="halls-cta">
          <p>Find the perfect hall for your event.</p>
          <a href="/private-events" className="post-review-btn">
            Learn More
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
